import format from 'date-fns/format';
import { enUS, es } from 'date-fns/locale';

import { Modal, Tooltip, Typography } from 'antd';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { RefundableEnum } from '@common-types';
import { RootState, renderNumber, formatDateCheckInOut, isSpanish } from '@share/utils';
import { Currency } from '@components';
import { DATE_FORMAT_CHECK_IN_OUT } from '@constants';

import { quoteCondoHasFutureAvailability, useQuoteCondosModalCompare } from './useQuoteCondosModalCompare';

import './style.scss';

const modalWidth = 900;
const ONE = 1;
const DATE_FORMAT = 'd MMM';
const percents = 100;

interface IProps {
  visible: boolean;
  quoteBase: string;
  isHistoricalTab: boolean;
  onCancel: () => void;
}

export function QuoteCondosModalCompare(props: IProps) {
  const { visible, quoteBase, isHistoricalTab, onCancel } = props;

  const quoteStore = useSelector((state: RootState) => state.quoteStore);
  const loginStore = useSelector((state: RootState) => state.loginStore);

  const {
    myQuotes,
    isHistorical,
    fontFamily,
    secondaryFontFamily,
    handleView,
    modalOpen,
  } = useQuoteCondosModalCompare({ quoteStore, loginStore, quoteBase, isHistoricalTab });

  const first = myQuotes[0];

  return (
    <Modal
      className={`condo-modal-compare ${fontFamily ? `${fontFamily}-font` : ''} ${secondaryFontFamily ? `secondary-${secondaryFontFamily}-font` : ''}`}
      open={visible && modalOpen}
      footer={null}
      onCancel={onCancel}
      wrapClassName="condo-modal-compare__wrapper"
      width={modalWidth}
    >
      <div>
        <div className="condo-modal-compare__title-row">
          <h4 className="condo-modal-compare__title secondary-font">
            <FormattedMessage id="results.condos.card.compare.modal.title" />
          </h4>
        </div>

        <div className="condo-modal-compare__hotels condo-modal-compare__hotels--meta">
          <div className="hotels-dates">
            {first?.checkIn && first?.checkOut ? (
              <>
                {formatDateCheckInOut(first.checkIn, DATE_FORMAT_CHECK_IN_OUT, isSpanish() ? es : undefined)} -{' '}
                {formatDateCheckInOut(first.checkOut, DATE_FORMAT_CHECK_IN_OUT, isSpanish() ? es : undefined)}
              </>
            ) : (
              '—'
            )}
          </div>
          <div className="hotels-dates">{first?.occupancy || ''}</div>
        </div>

        <div className="condo-modal-compare__hotels">
          {myQuotes.map((quote: any, index: number) => {
            const savings =
              quote.publicPrice && quote.price && quote.publicPrice > 0
                ? Math.floor(percents - (quote.price * percents) / quote.publicPrice)
                : 0;

            const canViewCondo = quoteCondoHasFutureAvailability(quote.availableDates);

            return (
              <div key={index} className="condo-modal-compare__hotel">
                <div className="condo-modal-compare__media">
                  <div className="condo-images-container">
                    <div className="condo-image" style={{ backgroundImage: `url(${quote.image})` }} />
                    {savings > 0 ? (
                      <div className="condo-modal-compare__savings-badge">
                        {savings.toFixed(0)} % <FormattedMessage id="results.hotel.card.compare.modal.off" />
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="condo-text-container condo-text-container--title">
                  <Tooltip title={quote?.displayName} placement="top">
                    <p>{quote?.displayName}</p>
                  </Tooltip>
                </div>

                <div className="condo-text-container condo-text-container--address">
                  <Tooltip title={quote?.fullAddress ?? ''} placement="top">
                    <p>{quote?.fullAddress}</p>
                  </Tooltip>
                </div>

                <div className="condo-text-container" style={{ height: isSpanish() ? '60px' : '40px' }}>
                  {quote?.availableDates?.length ? (
                    <div className="condo-modal-compare__date-label">
                      {quote.availableDates.length >= ONE && (
                        <div className="condo-modal-compare__date-more">
                          <p className="condo-modal-compare__date-more-text">
                            <Tooltip
                              className="condo-compare-tooltip"
                              overlayStyle={{ whiteSpace: 'pre-line' }}
                              placement="top"
                              title={
                                <>
                                  {quote.availableDates.map((d: any, i: number) => (
                                    <Typography key={i} style={{ color: '#FFFFFF' }}>
                                      {format(new Date(d.from), DATE_FORMAT, { locale: isSpanish() ? es : enUS })} -{' '}
                                      {format(new Date(d.to), DATE_FORMAT, { locale: isSpanish() ? es : enUS })}
                                    </Typography>
                                  ))}
                                </>
                              }
                            >
                              {quote.availableDates.length} <FormattedMessage id="available.dates" />
                            </Tooltip>
                          </p>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>

                {quote.refundability && quote.refundability !== RefundableEnum.Unknown ? (
                  <div className="condo-text-container condo-text-container--refundable">
                    <div className="hotel-card-wrapper__refundable">
                      {quote.refundability === RefundableEnum.Refundable && (
                        <div className="hotel-card-wrapper__refundable-ref">
                          <FormattedMessage id="refundable" />
                        </div>
                      )}
                      {quote.refundability === RefundableEnum.Nonrefundable && (
                        <div className="hotel-card-wrapper__refundable-nonref">
                          <FormattedMessage id="non.refundable" />
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}

                <div className="condo-amenities-container" style={{ height: isSpanish() ? '60px' : '43px' }}>
                  {(() => {
                    const ratingVal = quote.rating?.value ?? quote.rating;
                    const reviewsCount = quote.rating?.reviewsCount ?? quote.reviewsCount;
                    return ratingVal || reviewsCount ? (
                      <label>
                        {ratingVal != null ? (
                          <>
                            <strong>{Number(ratingVal).toFixed(1)}</strong> / 5
                          </>
                        ) : null}{' '}
                        {reviewsCount ? (
                          <>
                            ({reviewsCount} <FormattedMessage id="reviews" />)
                          </>
                        ) : null}
                      </label>
                    ) : (
                      <label>
                        <FormattedMessage id="reviews.guest.no.ratings" />
                      </label>
                    );
                  })()}
                </div>

                <div
                  className={`condo-text-container condo-text-container--price${
                    isHistorical ? ' condo-text-container--flush-bottom' : ''
                  }`}
                >
                  {quote.publicPrice ? (
                    <p style={{ textDecoration: 'line-through' }}>
                      <Currency currency={quote.currency} />
                      {renderNumber(quote.publicPrice, 2)}
                    </p>
                  ) : null}
                  <p>
                    <strong>
                      <Currency currency={quote.currency} />
                      {renderNumber(quote.price, 2)}
                    </strong>
                  </p>
                </div>

                {!isHistorical ? (
                  <div className="condo-view-container">
                    <div
                      className={`view-button${canViewCondo ? '' : ' view-button--disabled'}`}
                      onClick={canViewCondo ? () => handleView(quote) : undefined}
                      role="button"
                      aria-disabled={!canViewCondo}
                    >
                      <FormattedMessage id="results.hotel.card.compare.modal.view" />
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}
